import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { Item } from 'fc-ui-components';

import { ApiModel, Brand } from 'models';
import { MarketingFile } from 'models/brand/types';
import { LanguageService } from 'services';
import { BrandService } from 'services';
import { FileType } from 'helpers';
import { FiltersProvider, Filters } from './filters';
import { FolderCoverImage } from 'models/brand/types';
import { isExpired } from 'app/content/helpers/date';
import {
    ChannelName,
    MarketingFileSeason,
    MetadataUiMapper
} from 'app/content/services/marketing-files-search/metadata-ui-mapper';
@Injectable()
export class MarketingFilesSearchService {
    private metadataUiMapper: MetadataUiMapper;
    private filtersProvider: FiltersProvider;

    constructor(
        languageService: LanguageService,
        private apiModel: ApiModel,
        private brandsService: BrandService
    ) {
        this.metadataUiMapper = new MetadataUiMapper(languageService);
        this.filtersProvider = new FiltersProvider();
    }

    public mapMetadataToSeasonFilter(
        marketingFilesSeasons: MarketingFileSeason[],
        selectedSeasons: MarketingFileSeason[]
    ) {
        return this.metadataUiMapper.mapMetadataToSeasonFilter(marketingFilesSeasons, selectedSeasons);
    }

    public mapMetadataToExpiryDate(marketingFilesExpiryDates: string[]) {
        return this.metadataUiMapper.mapMetadataToExpiryDate(marketingFilesExpiryDates);
    }

    public mapMetadataToChannelsFilter(marketingFilesChannels: ChannelName[], selectedChannels: ChannelName[]) {
        return this.metadataUiMapper.mapMetadataToChannelsFilter(marketingFilesChannels, selectedChannels);
    }

    public mapMetadataToFileTypesFilter(marketingFileTypes: FileType[], selectedFileTypes: FileType[]) {
        return this.metadataUiMapper.mapMetadataToFileTypesFilter(marketingFileTypes, selectedFileTypes);
    }

    public isInFilters(filterValue: string | MarketingFileSeason, filters: Item[]) {
        return this.filtersProvider.isInFilters(filterValue, filters);
    }

    public fileIsShownByFilters(file, filters: Filters) {
        return this.filtersProvider.fileIsShownByFilters(file, filters);
    }

    public getMarketingFiles(brandId: string) {
        return this.apiModel.makeHttpObservableRequest<MarketingFile[]>({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/marketing-files`
        });
    }

    public getCategorizedMarketingFiles(brandId: string): Observable<CategoryMarketingFiles[]> {
        return combineLatest([this.brandsService.getBrand(brandId), this.getMarketingFiles(brandId)]).pipe(
            map(([brand, files]) => {
                const marketingFilesGroupedByCategory = _.groupBy(
                    files,
                    (file) => file.metadata.category || defaultCategory
                );

                const hasUncategorizedFiles = _.has(marketingFilesGroupedByCategory, defaultCategory);

                const coverDataByFolderName = brand.folders
                    ? brand.folders.reduce((coverDataAccumulator, folder) => {
                          coverDataAccumulator[folder.name] = folder.image;
                          return coverDataAccumulator;
                      }, {})
                    : [];

                return _.reduce(
                    [
                        ...(hasUncategorizedFiles ? [defaultCategory] : []),
                        ...Object.keys(coverDataByFolderName),
                        ..._.get(brand, 'categories', [])
                    ],
                    (categoriesWithFiles, category) => [
                        ...categoriesWithFiles,
                        ...[
                            {
                                brand,
                                files: marketingFilesGroupedByCategory[category] || [],
                                folderCoverData: coverDataByFolderName[category] || null,
                                name: category || defaultCategory,
                                isFolderExpired:
                                    this.checkIsFolderExpired(marketingFilesGroupedByCategory[category]) || false,
                                allFolderNames: {
                                    legacy: _.map(brand.categories, (category) => ({ name: category })),
                                    current: [..._.get(brand, 'folders', [])]
                                }
                            }
                        ]
                    ],
                    []
                );
            })
        );
    }

    public getMarketingFilesWithCategory(
        brandId: string,
        category: string | 'uncategorized',
        excludeExpired = true
    ): Observable<CategoryMarketingFiles> {
        const categoryName = category.toLowerCase() === defaultCategory ? defaultCategory : category;

        return combineLatest([this.brandsService.getBrand(brandId), this.getMarketingFiles(brandId)]).pipe(
            map(([brand, files]) => {
                const filesFilteredByCategory = files.filter(
                    ({ metadata }) => (metadata.category || defaultCategory) === categoryName
                );
                return {
                    brand,
                    files: excludeExpired
                        ? _.filter(
                              filesFilteredByCategory,
                              (marketingFile) => !isExpired(marketingFile.metadata.expirationDate)
                          )
                        : filesFilteredByCategory,
                    name: _.get(_.first(filesFilteredByCategory), 'metadata.category', defaultCategory)
                };
            })
        );
    }

    public filterMarketingFiles(files: MarketingFile[], filters: Filters) {
        return files.filter((file) => this.fileIsShownByFilters(file, filters));
    }

    private checkIsFolderExpired(files: MarketingFile[]): boolean | undefined {
        return files?.reduce(
            (isFolderExpired, file) => isExpired(isFolderExpired && file.metadata.expirationDate),
            true
        );
    }
}

export interface CategoryMarketingFiles {
    brand: Brand;
    files: MarketingFile[];
    name: string;
    folderCoverData?: FolderCoverImage;
    isFolderExpired?: boolean;
    allFolderNames?: { legacy: { name: string }[]; current: { _id: string; name: string }[] };
}

export const defaultCategory = 'uncategorized';
